import { EventBus } from "../../contexts";
import { logger, LOGS } from "../../utils";
import { filterNoReplyContactsForQuery } from "../../utils/contactFilter/contactFilter";
import {
    dedupAttachments,
    dedupLinks,
    filterFutureEvents,
} from "../../utils/viewContentDedup";
import { ApiManager } from "../api";
import { ContactManager } from "../contact";
import { DataManager } from "../dataManager/DataManager";
import { IaManager } from "../ia";
import { parseUniqueMailvistaId } from "../mail/domain/mailIdentity";
import { MailSync } from "../mail/MailSync";
import {
    ACTIVE_SMART_LABELS,
    AttachmentMetadata,
    LinkedEventDetails,
    LinkObject,
    Mail,
    MailViewContent,
    SmartLabel,
} from "../mail/types/mail";
import { FeatureFlag } from "../user";
import { FeatureFlags } from "../user/FeatureFlags";
import { UserContextLoader } from "../user/UserContextLoader";
import { CustomViewMembership } from "./CustomViewMembership";
import { CustomViewState } from "./CustomViewState";
import {
    sanitizeExclusiveSmartLabelEntries,
    toIdArray,
} from "./smartLabelEntry";
import { SmartLabelStore } from "./SmartLabelStore";
import { CustomViewTodoItemDTO } from "./types/customViewData.api";
import {
    CustomView,
    CustomViewInput,
    CustomViewPatchParams,
    CustomViewType,
    emptyCustomView,
    SmartLabelEntry,
    StartupMails,
    ViewFilter,
} from "./types/customViews";
import { viewFiltersAreSame } from "./viewFilter";

export class CustomViewManager {
    // #region Properties

    private eventBus: EventBus;
    private iaManager: IaManager;
    private dataManager: DataManager;
    private mailSync: MailSync;
    private featureFlags: FeatureFlags;

    private contextLoader: UserContextLoader;
    private apiManager: ApiManager;
    private contactManager: ContactManager;

    private userMail: string;

    private smartLabelStore: SmartLabelStore;

    private viewState: CustomViewState;

    private membership: CustomViewMembership;

    private isInitialisationDone = false;

    /** Signature des champs « pertinents pour les vues » du dernier `mail-updated` traité, par internetMessageId.
     *  Sert à ignorer les `mail-updated` qui ne changent QUE le contenu d'affichage (ex: résolution des
     *  images inline → mute message/uniqueBody) : dans ce cas l'appartenance et l'ordre des vues sont
     *  inchangés, inutile de réinsérer/re-matcher (cf. _computeMailViewSignature & mail-updated_handler).
     *  On stocke la signature (string) plutôt qu'un hash : ~150 car/mail, négligeable face au Record qui
     *  garde les Mail complets (bodies inclus), et sans risque de collision ni coût de hachage.
     *  Purge : par id sur `mail-deleted`, et clear complet au cleanup() → bornée par les mails vivants. */
    private readonly _mailViewSignatures = new Map<string, string>();

    // #endregion Properties

    constructor(
        eventBus: EventBus,
        userMail: string,
        smartLabelStore: SmartLabelStore,
        viewState: CustomViewState,
        membership: CustomViewMembership,
        iaManager: IaManager,
        dataManager: DataManager,
        mailSync: MailSync,
        featureFlags: FeatureFlags,
        contextLoader: UserContextLoader,
        apiManager: ApiManager,
        contactManager: ContactManager,
    ) {
        logger.info(LOGS.CustomViewManager, "CustomViewManager()");
        this.eventBus = eventBus;
        this.userMail = userMail;
        this.smartLabelStore = smartLabelStore;
        this.viewState = viewState;
        this.membership = membership;
        this.iaManager = iaManager;
        this.dataManager = dataManager;
        this.mailSync = mailSync;
        this.featureFlags = featureFlags;
        this.contextLoader = contextLoader;
        this.apiManager = apiManager;
        this.contactManager = contactManager;
    }

    public _getStartupMails(): StartupMails {
        logger.info(
            [LOGS.CustomViewManager, LOGS.TESTS],
            `_getStartupMails()`,
            this.viewState.startupMails,
        );

        return this.viewState.startupMails;
    }

    public _getSmartLabelInternetMessageIds(
        label: SmartLabel,
        from?: number,
        top?: number,
    ): string[] {
        return this.membership._getSmartLabelInternetMessageIds(
            label,
            from,
            top,
        );
    }

    /**
     * Lance un refresh de la vue quand la première page est demandée.
     */
    public _getInternetMessageIdsFromCustomView(
        customViewId: string,
        from?: number,
        top?: number,
    ): Promise<string[]> {
        return this.membership._getInternetMessageIdsFromCustomView(
            customViewId,
            from,
            top,
        );
    }

    public getView(customViewId: string): Promise<CustomView> {
        return this.membership.getView(customViewId);
    }

    // #region Initialisation and cleanup

    /**
     * Fetch all customViews from DB + SmartLabels depuis le nouveau endpoint.
     * Calculate StartupMails list.
     */
    public async _initialize(): Promise<void> {
        logger.info(
            [LOGS.CustomViewManager, LOGS.ServiceProvider],
            `CustomviewManager.initialize()`,
        );

        const startTime = performance.now();

        // 1. Fetch toutes les customViews (uniquement viewType === Custom depuis la nouvelle archi backend)
        const customViews = (await this._getAllCustomViews()).filter(
            (v) => v.viewType === CustomViewType.Custom,
        );
        logger.perf(
            LOGS.CustomViewManager,
            "CustomViewManager.Initilize => _getAllCustomViews done",
            startTime,
        );

        customViews.forEach(
            (customView: CustomView) =>
                (this.viewState.viewsRecordById[customView.id!] = customView),
        );
        logger.info(
            [LOGS.CustomViewManager],
            `   CustomView Record updated after _initialize()`,
        );
        logger.info(
            [LOGS.CustomViewManager],
            `  this.viewState.viewsRecordById`,
            JSON.parse(JSON.stringify(this.viewState.viewsRecordById)),
        );

        // 2. Fetch les SmartLabels via le nouveau endpoint GET /v1/smartLabels

        try {
            const smartLabelsFromApi =
                await this.apiManager.getSmartLabelsForUser();
            // Stocker dans le store interne — déjà triés par le backend, ne pas re-trier.
            // [#1672 — fix ① exclusivité] On assainit un éventuel double-assignement backend (même id dans
            // ≥ 2 labels) : P2/INV-4 impose « au plus un label par id ». Voir sanitizeExclusiveSmartLabelEntries.
            const sanitized =
                sanitizeExclusiveSmartLabelEntries(smartLabelsFromApi);
            for (const label of ACTIVE_SMART_LABELS) {
                this.smartLabelStore.entries[label] = sanitized[label] ?? [];
            }
            logger.info(
                [LOGS.CustomViewManager],
                `SmartLabels chargés — ${ACTIVE_SMART_LABELS.map((l) => `${l}:${this.smartLabelStore.entries[l]?.length ?? 0}`).join(", ")}`,
            );
        } catch (error) {
            logger.error(
                LOGS.CustomViewManager,
                `_initialize() — échec du chargement des SmartLabels : ${error}`,
            );
            // Non bloquant : on initialise les stores à vide
            for (const label of ACTIVE_SMART_LABELS) {
                this.smartLabelStore.entries[label] = [];
            }
        }

        // 3. Calculer les IDs de mails à pré-charger au démarrage

        // Focus — IDs issus du nouveau store SmartLabel
        const focusMailsInternetMessageIds = toIdArray(
            this.smartLabelStore.entries[SmartLabel.Focus] ?? [],
        );

        // Favorites — IDs issus des CustomViews (inchangé)
        const favoriteCustomViewsMailsInternetMessageIds = [
            ...new Set(
                Object.values(this.viewState.viewsRecordById)
                    .filter(
                        (customView) =>
                            customView.viewType === CustomViewType.Custom &&
                            customView.isFavorite === true,
                    )
                    .flatMap(
                        (view) => view.internetMessageIds.slice(0, 16) ?? [],
                    ),
            ),
        ];

        // Other views — IDs issus des CustomViews (inchangé)
        const otherViewsMailsInternetMessageIds = [
            ...new Set(
                Object.values(this.viewState.viewsRecordById)
                    .filter(
                        (customView) =>
                            customView.viewType === CustomViewType.Custom &&
                            customView.isFavorite === false,
                    )
                    .flatMap(
                        (view) => view.internetMessageIds.slice(0, 16) ?? [],
                    ),
            ),
        ];

        logger.perf(
            LOGS.CustomViewManager,
            "CustomViewManager.Initilize() done",
            startTime,
        );

        this.viewState.startupMails = {
            focusMailsInternetMessageIds: focusMailsInternetMessageIds,
            favoriteCustomViewsMailsInternetMessageIds:
                favoriteCustomViewsMailsInternetMessageIds,
            otherViewsMailsInternetMessageIds:
                otherViewsMailsInternetMessageIds,
        };

        this.isInitialisationDone = true;
    }

    /**
     * @method cleanup
     * Cleans up all resources and stops all active processes for the CustomViewManager
     * ═══════════════════════════════════════════════════════════════════════════
     * @input {void}
     * @output {Promise<void>}
     * @public
     */
    public async _cleanup(): Promise<void> {
        logger.info(
            LOGS.CustomViewManager,
            "CustomViewManager.cleanup() - Starting cleanup process",
        );

        // Phase 1: Stop all active processes and timers
        try {
            logger.info(
                LOGS.CustomViewManager,
                "  Phase 1: Stopping active processes",
            );

            // No timers, intervals, or polling to stop for CustomViewManager
            // All operations are request-based
        } catch (error) {
            logger.error(
                LOGS.CustomViewManager,
                `CustomViewManager.cleanup() - Error in Phase 1: ${error}`,
            );
        }

        // Phase 2: Clear internal state and caches
        try {
            logger.info(
                LOGS.CustomViewManager,
                "  Phase 2: Clearing internal state",
            );

            // Reset user mail to initial state
            this.userMail = "unknown";

            // Clear View Record
            this.viewState.viewsRecordById = {};
            this.membership._cleanup();
            this._mailViewSignatures.clear();
        } catch (error) {
            logger.error(
                LOGS.CustomViewManager,
                `CustomViewManager.cleanup() - Error in Phase 2: ${error}`,
            );
        }

        logger.info(
            LOGS.CustomViewManager,
            "CustomViewManager.cleanup() - Cleanup completed successfully",
        );
    }

    // #endregion Initialisation and cleanup

    // #region Event Handlers

    public _registerEvents() {
        this["mail-updated_handler"] = this["mail-updated_handler"].bind(this);
        this["mail-deleted_handler"] = this["mail-deleted_handler"].bind(this);

        this.eventBus.on("mail-updated", this["mail-updated_handler"]);
        this.eventBus.on("mail-deleted", this["mail-deleted_handler"]);
    }

    private async "mail-updated_handler"(mail: Mail): Promise<void> {
        logger.info(
            [LOGS.CustomViewManager],
            `CustomViewManager processing <mail-updated> event : ${mail.internetMessageId}...`,
        );

        // Court-circuit « contenu seul » : si aucun champ pertinent pour les vues (appartenance/ordre)
        // n'a changé depuis le dernier mail-updated, on ne touche NI aux SmartLabels NI aux CustomViews.
        // Cas typique : l'enrichissement des images inline (résolution CID→data URI) mute message/uniqueBody
        // puis émet mail-updated → le front en a besoin pour réafficher le mail (sa propre souscription le
        // patche), mais réinsérer le mail dans le store / re-matcher les vues serait inutile (et provoquait
        // un faux saut d'ordre en Focus + refetch liste + recompute navbar superflus).
        // Première occurrence (signature absente) : on procède et on seed.
        const newSignature = this._computeMailViewSignature(mail);
        const prevSignature = this._mailViewSignatures.get(
            mail.internetMessageId,
        );
        this._mailViewSignatures.set(mail.internetMessageId, newSignature);

        if (prevSignature !== undefined && prevSignature === newSignature) {
            logger.info(
                [LOGS.CustomViewManager],
                `  mail-updated content-only (signature vues inchangée) → skip mise à jour SmartLabels/CustomViews`,
            );
            return;
        }

        this.membership._updateSmartLabelEntryAndSendEvent(mail);
        await this.membership._updateAllCustomViewsIfNeededAndSendEvent(mail);
    }

    /**
     * Construit une signature stable des champs d'un mail qui influent sur l'APPARTENANCE ou
     * l'ORDRE d'une vue (SmartLabel ou CustomView). Deux mails de même signature donnent le même
     * placement dans toutes les vues.
     *
     * Volontairement EXCLUS : message, uniqueBody, hasAttachment, attachments — ce sont des champs
     * de contenu/affichage, mutés par l'enrichissement PJ inline sans impact sur le tri ni le matching.
     * Note : une CustomView peut filtrer sur des mots-clés du body, mais la substitution CID→data URI
     * ne change pas le TEXTE → pas d'impact mots-clés ; exclure le body est donc sûr en pratique.
     */
    /**
     * Amorce la signature de vues d'un mail AVANT un `mail-updated` que l'on sait « contenu seul »
     * (typiquement l'enrichissement des images inline). En enregistrant la signature de l'état
     * courant juste avant l'emit, le `mail-updated_handler` recalcule une signature identique →
     * court-circuit (pas de réinsertion ni de refetch). Sans cet amorçage, l'enrichissement étant
     * le PREMIER mail-updated du mail (guard `_attachmentsFetchedSet`), le handler n'aurait aucune
     * signature de référence et procéderait (cascade inutile + faux saut d'ordre en Focus).
     */
    public primeMailViewSignature(mail: Mail): void {
        this._mailViewSignatures.set(
            mail.internetMessageId,
            this._computeMailViewSignature(mail),
        );
    }

    private _computeMailViewSignature(mail: Mail): string {
        return [
            mail.smartLabel ?? "",
            mail.isUserProcessed ? "1" : "0",
            mail.isFocus ? "1" : "0",
            mail.isRead ? "1" : "0",
            mail.sentDateTime ?? "",
            mail.sentDateTimeMailvista ?? "",
            mail.manualFocusDate ?? "",
            mail.score ?? "",
            mail.parentFolderId ?? "",
            mail.subject ?? "",
            (mail.aiDetails?.categories ?? []).join(","),
        ].join("|");
    }

    /**
     * Réaction à la suppression d'un mail (évincé par MailManager : supprimé côté provider,
     * en corbeille, ou introuvable).
     *
     * WS5c : l'event porte l'identité de COPIE supprimée (`uniqueMailvistaId`) ; on en dérive
     * l'`internetMessageId`. L'émetteur (MailManager) garantit que le Record reflète DÉJÀ la suppression
     * (émission post-mutation) → on peut interroger les **copies survivantes** du message pour décider.
     *
     * WS5c-2 — retrait CONDITIONNEL (§2.4) :
     *  - **SmartLabel** : retiré SEULEMENT si plus aucune copie inbox (canonique) ne survit
     *    (suppression d'une copie sent/trash → no-op SmartLabel). Insensible à la dette de re-key WS3 :
     *    on teste `copy.parentFolderId` (champ objet correct), pas la clé de slot.
     *  - **CustomView** : retiré d'une vue SEULEMENT si aucune copie survivante ne matche encore son filtre
     *    (`_isMailMatchCustomView`, déjà copie-conscient — exclut Deleted Items).
     */
    private async "mail-deleted_handler"(payload: {
        uniqueMailvistaId: string;
    }): Promise<void> {
        const { internetMessageId, parentFolderId: deletedFolderId } =
            parseUniqueMailvistaId(payload.uniqueMailvistaId);
        logger.info(
            [LOGS.CustomViewManager],
            `CustomViewManager processing <mail-deleted> event : ${payload.uniqueMailvistaId}`,
        );

        // [#1672 — fix ④ RACE-1710-C] Guard robuste à l'ordre Record/event : on EXCLUT explicitement la copie
        // qui vient d'être supprimée (slot `deletedFolderId`), qu'elle soit ou non déjà purgée du Record.
        // Historiquement on supposait que l'émetteur (MailManager) avait DÉJÀ purgé le Record ; si l'event
        // arrive avant la purge, la copie inbox supprimée « survivait » à tort → SmartLabel jamais nettoyé
        // (compteur Focus fantôme). En filtrant le slot supprimé, le verdict est déterministe.
        const survivingCopies = this.mailSync
            ._getCopiesForMessage(internetMessageId)
            .filter((copy) => copy.parentFolderId !== deletedFolderId);
        const inboxFolderId = this.mailSync._getInboxFolderId();
        const inboxCopySurvives =
            inboxFolderId !== undefined &&
            survivingCopies.some(
                (copy) => copy.parentFolderId === inboxFolderId,
            );

        // 1. SmartLabel : retrait seulement si la copie inbox canonique est partie (§2.4 règle 2).
        if (!inboxCopySurvives) {
            this.smartLabelStore.removeMessage(internetMessageId);
        }

        // 2. CustomViews utilisateur : retrait conditionnel (§2.4 règle 3).
        await this.membership._removeInEveryCustomViews(
            internetMessageId,
            survivingCopies,
        );

        // 3. Purge de la signature de vues (cache de détection de changement) — borne la map aux mails vivants.
        this._mailViewSignatures.delete(internetMessageId);
    }

    public _snapshotSmartLabelEntriesById(
        internetMessageIds: string[],
    ): Map<string, SmartLabelEntry> {
        return this.smartLabelStore.snapshotEntriesById(internetMessageIds);
    }

    public _removeMessageFromSmartLabelStoreIfUnchanged(
        internetMessageId: string,
        snapshotEntry: SmartLabelEntry | undefined,
    ): boolean {
        return this.smartLabelStore.removeMessageIfUnchanged(
            internetMessageId,
            snapshotEntry,
        );
    }

    public _getAllSmartLabelInternetMessageIds(): string[] {
        return this.smartLabelStore.getAllInternetMessageIds();
    }

    // #endregion Event Handlers

    // #region CustomView Object and properties management

    /**
     * Creates default SmartLabelCustomViews views for the current user
     *
     * @returns Promise with array of SmartLabel CustomView objects
     */
    public async _createSmartLabelCustomViews(): Promise<void> {
        const smartLabelView: CustomViewInput = emptyCustomView;

        try {
            // Focus
            smartLabelView.userId = this.userMail.toLowerCase();
            smartLabelView.name = CustomViewType.Focus;
            smartLabelView.icon = "";
            smartLabelView.viewType = undefined;
            ((smartLabelView.widgets = {
                showUpcomingMeetings: false,
                showTodo: false,
                showDocuments: false,
                showLinks: false,
                showNotes: false,
            }),
                await this.createView(smartLabelView, CustomViewType.Focus));

            // Pour info
            smartLabelView.name = CustomViewType.PourInfo;
            await this.createView(smartLabelView, CustomViewType.PourInfo);

            // Newsletter
            smartLabelView.name = CustomViewType.Newsletter;
            await this.createView(smartLabelView, CustomViewType.Newsletter);

            // Suspect
            // smartLabelView.name     = CustomViewType.Suspect;
            // await this.createView(smartLabelView, CustomViewType.Suspect);

            // Autre
            smartLabelView.name = CustomViewType.Autre;
            await this.createView(smartLabelView, CustomViewType.Autre);

            // Traités
            smartLabelView.name = CustomViewType.Traites;
            await this.createView(smartLabelView, CustomViewType.Traites);
        } catch (error) {
            logger.error(
                LOGS.CustomViewManager,
                "Error during SmartLabel CustomViews creation",
                error,
            );
            throw error;
        }
    }

    /**
     * Get user defined customViews only
     * @returns Promise with array of CustomView objects
     */
    public async getCustomViews(): Promise<CustomView[]> {
        logger.info(LOGS.CustomViewManager, "getCustomViews()");

        const allcustomViews = await this._getAllCustomViews();
        const userCustomViews = allcustomViews.filter(
            (customView) => customView.viewType == CustomViewType.Custom,
        );

        return userCustomViews;
    }

    /**
     * Get all custom views for the current user
     * @returns Promise with array of CustomView objects
     */
    public async _getAllCustomViews(): Promise<CustomView[]> {
        logger.info(LOGS.CustomViewManager, "_getAllCustomViews()");

        try {
            // Check dans le Record
            const recordViews: CustomView[] = Object.values(
                this.viewState.viewsRecordById,
            );

            if (recordViews.length > 0) {
                logger.info(
                    LOGS.CustomViewManager,
                    " returning customViews from Record ...",
                    recordViews,
                );
                return recordViews;
            }

            // Si pas trouvé dans le Record, on est dans le startup du CustomViewManager. On fetch depuis le dbManager

            const allcustomViews: CustomView[] =
                await this.apiManager.getCustomViewsForUser();
            logger.info(
                LOGS.CustomViewManager,
                " returning customViews from DB ...",
                allcustomViews,
            );

            return allcustomViews;
        } catch (error) {
            logger.error(
                LOGS.CustomViewManager,
                "Error retrieving all custom views",
                error,
            );
            throw error;
        }
    }

    /**
     * Get number of custom views user defined only
     * @returns Promise with array of CustomView objects
     */
    public async _getCustomViewsCount(): Promise<number> {
        logger.info(LOGS.CustomViewManager, "getCustomViewsCount()");

        try {
            const numberOfCustomViews: number =
                await this.apiManager.getCustomViewsCount();
            logger.info(
                LOGS.CustomViewManager,
                `There are ${numberOfCustomViews} customViews from DB`,
            );

            return numberOfCustomViews;
        } catch (error) {
            logger.error(
                LOGS.CustomViewManager,
                "Error retrieving number of custom views",
                error,
            );
            throw error;
        }
    }

    /**
     * Get favorite custom views for the current user, from Record, then from DB if needed
     * @returns Promise with array of favorite CustomView objects
     */
    public async _getFavoriteViews(): Promise<CustomView[]> {
        logger.info(LOGS.CustomViewManager, "getFavoriteViews()");

        try {
            // On check dans le Record en premier
            const recordFavoriteViews = Object.values(
                this.viewState.viewsRecordById,
            ).filter((view) => view.isFavorite);

            if (recordFavoriteViews.length > 0) {
                return recordFavoriteViews;
            }

            // Si pas de résultat dans le Record, récupération en DB

            const result: CustomView[] =
                await this.apiManager.getFavoriteCustomViewsForUser();
            logger.info(
                LOGS.CustomViewManager,
                "favoriteViews from DB",
                result,
            );

            return result;
        } catch (error) {
            logger.error(
                LOGS.CustomViewManager,
                "Error retrieving favorite views",
                error,
            );
            throw error;
        }
    }

    /**
     * Set the order of favorite views for the current user
     * @param orderedViewsIds Array of view IDs in the desired order
     * @returns Promise
     */
    public async setFavoriteViews(orderedViewsIds: string[]): Promise<void> {
        logger.info(LOGS.CustomViewManager, "setFavoriteViews()");

        try {
            const updatedViews =
                await this.apiManager.setFavoriteCustomViews(orderedViewsIds);

            // On remplace dans le Record l'ordre des customView favorites
            const recordFavoriteViews = Object.values(
                this.viewState.viewsRecordById,
            ).filter((view) => view.isFavorite);

            if (recordFavoriteViews.length > 0) {
                // Delete puis re-insertion dans le nouvel ordre — nécessaire car Object.values()
                // retourne les clés en ordre d'insertion, pas selon displayOrder.
                // La membership en mémoire est conservée : la réponse backend a pu être sérialisée
                // avant la dernière écriture d'analyse, et la reprendre l'écraserait.
                // Identifiants et composition datée disent la même chose : n'en mettre qu'une
                // à jour laisserait des dates périmées, sur lesquelles la prochaine écriture se
                // reconstruirait.
                const membershipByViewId = new Map<
                    string,
                    Pick<CustomView, "internetMessageIds" | "mails">
                >();

                updatedViews.forEach((view) => {
                    if (!view.id) return;
                    const current = this.viewState.viewsRecordById[view.id];
                    if (current?.internetMessageIds) {
                        membershipByViewId.set(view.id, {
                            internetMessageIds: current.internetMessageIds,
                            mails: current.mails ?? [],
                        });
                    }
                    delete this.viewState.viewsRecordById[view.id];
                });
                updatedViews.forEach((view) => {
                    if (!view.id) return;
                    const membership = membershipByViewId.get(view.id);
                    this.viewState.viewsRecordById[view.id] = membership
                        ? { ...view, ...membership }
                        : view;
                });
            }

            updatedViews.forEach((view) => {
                this.eventBus.emit(
                    "custom-view-updated",
                    JSON.parse(
                        JSON.stringify(
                            (view.id &&
                                this.viewState.viewsRecordById[view.id]) ||
                                view,
                        ),
                    ),
                );
            });
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            if (errorMessage.includes("NO_VALID_LICENSE_FOR_CUSTOM_VIEW")) {
                this.eventBus.emit("no-valid-license-for-custom-view");
                throw error;
            }
            logger.error(
                LOGS.CustomViewManager,
                "Error setting favorite views",
                error,
            );
            throw error;
        }
    }

    /**
     * Create a new custom view
     * @param customView     CustomView object to create
     * @param customViewType Optionnal, should only be used when creating default SmartLabel CustomViews
     * @returns Promise
     */
    public async createView(
        customView: CustomViewInput,
        customViewType: CustomViewType = CustomViewType.Custom,
    ): Promise<CustomView> {
        logger.info(LOGS.CustomViewManager, "createView()");
        this._assertHasValidLicenseForCustomView();

        try {
            // Une vue naît vide : sa composition est calculée par le recalcul qui suit.
            customView.internetMessageIds = [];
            customView.viewType = customViewType;
            const customViewResult: CustomView =
                await this.apiManager.createCustomView(customView);

            // Add new customView to Record
            if (customViewResult.id) {
                this.viewState.viewsRecordById[customViewResult.id] =
                    customViewResult;
            }

            this.eventBus.emit("custom-view-created", customViewResult);

            return customViewResult;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            if (errorMessage.includes("NO_VALID_LICENSE_FOR_CUSTOM_VIEW")) {
                this.eventBus.emit("no-valid-license-for-custom-view");
                throw error;
            }
            logger.error(
                LOGS.CustomViewManager,
                "Error creating custom view",
                error,
            );
            throw error;
        }
    }

    /**
     * Edit an existing custom view
     * @param customViewId ID of the view to edit
     * @param updatedCustomView Updated CustomView object
     * @returns Promise
     */
    public async editView(
        customViewId: string,
        updatedCustomView: CustomView,
    ): Promise<void> {
        logger.info(LOGS.CustomViewManager, `editView(${customViewId})`);

        // Look for existing view
        const existingCustomview = this.viewState.viewsRecordById[customViewId];
        if (!existingCustomview) {
            const error = `CustomViewManager.EditView fail to retrieve view (${customViewId}) to edit from Record, should not happen !`;
            logger.error(LOGS.CustomViewManager, error);
        }

        // Save a copy of the original filters for later comparison
        const originalFilters = JSON.parse(
            JSON.stringify(existingCustomview.filters),
        );

        try {
            // Build patch params with only the fields that have changed
            const patchParams: CustomViewPatchParams = {};

            // List of fields to compare (excluding internetMessageIds)
            // `mails` exclu au même titre que `internetMessageIds` : la composition ne se met
            // à jour que par `_overrideIfNeeded…`, jamais par une comparaison de champs.
            const fieldsToCompare: (keyof Omit<
                CustomView,
                "id" | "internetMessageIds" | "mails"
            >)[] = [
                "userId",
                "name",
                "icon",
                "filters",
                "displayMode",
                "widgets",
                "createdAt",
                "updatedAt",
                "isFavorite",
                "displayOrder",
                "userPrompt",
                "noteContent",
            ];

            // Compare each field and add to patchParams if different
            fieldsToCompare.forEach((field) => {
                if (
                    JSON.stringify(existingCustomview[field]) !==
                    JSON.stringify(updatedCustomView[field])
                ) {
                    (patchParams as any)[field] = updatedCustomView[field];
                }
            });

            // Filtres normalisés par le backend, renseignés une fois le PATCH effectué.
            let persistedFilters: ViewFilter | undefined;

            // Only update if there are actual changes
            if (Object.keys(patchParams).length > 0) {
                const customViewUpdated = await this.apiManager.patchCustomView(
                    customViewId,
                    patchParams,
                );

                // Le backend y garantit `sortOrder` (défaut DESC côté DTO et à la relecture).
                persistedFilters = customViewUpdated.filters;

                // Update local cache with only the modified fields
                this.viewState.viewsRecordById[customViewId] = {
                    ...this.viewState.viewsRecordById[customViewId],
                    ...patchParams,
                    ...(patchParams.filters
                        ? { filters: persistedFilters }
                        : {}),
                };

                // Emit event
                this.eventBus.emit("custom-view-updated", customViewUpdated);
            }

            // Seulement si le ViewFilter a changé
            if (
                !viewFiltersAreSame(originalFilters, updatedCustomView.filters)
            ) {
                logger.info(LOGS.CustomViewManager, "  ViewFilter Changed", {
                    oldFilters: originalFilters,
                    newFilters: updatedCustomView.filters,
                });

                // Update Filter in Record — sur l'objet du Record, qui a pu être recréé juste au-dessus.
                logger.info(
                    LOGS.CustomViewManager,
                    "  => updating filter in Record,",
                );
                this.viewState.viewsRecordById[customViewId].filters =
                    persistedFilters ?? updatedCustomView.filters;

                // Refresh membership with new filter (lightweight — IDs only, no mail content fetch)
                logger.info(
                    LOGS.CustomViewManager,
                    "  refreshing customView membership",
                );
                void this.membership
                    ._refreshViewIds(customViewId)
                    .catch((error) =>
                        logger.error(
                            LOGS.CustomViewManager,
                            `_refreshViewIds(${customViewId}) en tâche de fond : ${error}`,
                        ),
                    );
            }
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            if (errorMessage.includes("NO_VALID_LICENSE_FOR_CUSTOM_VIEW")) {
                this.eventBus.emit("no-valid-license-for-custom-view");
                throw error;
            }
            logger.error(
                LOGS.CustomViewManager,
                "Error editing custom view",
                error,
            );
            throw error;
        }
    }

    /**
     * Delete a custom view
     * @param customViewId ID of the view to delete
     * @returns Promise
     */
    public async deleteView(customViewId: string): Promise<void> {
        logger.info(LOGS.CustomViewManager, `deleteView(${customViewId})`);

        // Boolean to check if CustomView can be deleted (cannot delete SmartLabels)
        let enableToDeleteCustomView: boolean = false;

        // Clear Record
        if (this.viewState.viewsRecordById[customViewId]) {
            if (
                this.viewState.viewsRecordById[customViewId].viewType ===
                CustomViewType.Custom
            ) {
                enableToDeleteCustomView = true;

                delete this.viewState.viewsRecordById[customViewId];
                logger.info(
                    [LOGS.CustomViewManager],
                    `Cache cleared for customView ${customViewId}`,
                );
            } else {
                logger.warn(
                    LOGS.CustomViewManager,
                    `Unable to clear customView ${this.viewState.viewsRecordById[customViewId].name} from Record...`,
                );
            }
        } else {
            logger.info(
                [LOGS.CustomViewManager],
                `No cache found to clear for customView ${customViewId}`,
            );
        }

        // Clear DB
        try {
            if (enableToDeleteCustomView) {
                await this.apiManager.deleteCustomView(customViewId);

                this.eventBus.emit("custom-view-deleted", customViewId);
            } else {
                logger.warn(
                    LOGS.CustomViewManager,
                    `Unable to delete customView ${customViewId} from DB...`,
                );
            }
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            if (errorMessage.includes("NO_VALID_LICENSE_FOR_CUSTOM_VIEW")) {
                this.eventBus.emit("no-valid-license-for-custom-view");
                throw error;
            }
            logger.error(
                LOGS.CustomViewManager,
                "Error deleting custom view",
                error,
            );
            throw error;
        }
    }

    /**
     * Get all todos extracted from the mails belonging to a custom view.
     * Each todo carries its `mailId` so it can be linked back to its source mail.
     * No business rule applied for now — delegates to the API.
     * @param customViewId ID of the view whose mails' todos are retrieved
     * @returns Promise with a flat array of the view's mail todos
     */
    public async getViewTodos(
        customViewId: string,
    ): Promise<CustomViewTodoItemDTO[]> {
        logger.info(LOGS.CustomViewManager, `getViewTodos(${customViewId})`);

        try {
            const viewTodos: CustomViewTodoItemDTO[] =
                await this.apiManager.getCustomViewTodos(customViewId);
            return viewTodos;
        } catch (error) {
            logger.error(
                LOGS.CustomViewManager,
                "Error retrieving custom view todos",
                error,
            );
            throw error;
        }
    }

    /**
     * Résout la liste COMPLÈTE des internetMessageIds d'une view (membership = source de vérité),
     * garantit leur contenu dans le Record par-mail (fetch des seuls absents),
     * puis renvoie les bundles bruts par mail. Base commune aux 3 lecteurs docs/links/events.
     */
    private async _getMailViewContentsForView(
        customViewId: string,
    ): Promise<readonly MailViewContent[]> {
        const view = await this.membership.getView(customViewId);
        const internetMessageIds = view.internetMessageIds ?? [];
        logger.info(
            LOGS.AB1727,
            `[AB#1727-SDK] view membership resolved (view=${customViewId}) internetMessageIds=${internetMessageIds.length}`,
        );
        if (internetMessageIds.length === 0) return [];

        const allBatchesStart = performance.now();
        await this.mailSync.fetchMissingMailViewContentIfAny(
            internetMessageIds,
        );
        logger.perf(
            LOGS.CustomViewManager,
            `[timing] _getMailViewContentsForView(${customViewId}) - fetch de TOUS les batchs contenu (${internetMessageIds.length} mails)`,
            allBatchesStart,
        );
        return this.mailSync.getMailViewContentFromRecord(internetMessageIds);
    }

    /**
     * Récupère l'INTÉGRALITÉ des pièces jointes (métadonnées légères, sans preview) d'une customView,
     * indépendamment de la pagination front. Agrège le contenu par-mail (cache MailManager) et
     * DÉDUPLIQUE au read (par contentId, sinon nom+type — la taille varie au transfert, cf. AB#1681).
     * La preview se demande ensuite via `MailManager.getAttachmentPreview(s)`.
     * @param customViewId ID de la view
     * @returns Promise avec les métadonnées dédupliquées de toutes les pièces jointes de la view
     */
    public async getCustomViewAttachments(
        customViewId: string,
    ): Promise<AttachmentMetadata[]> {
        logger.info(
            LOGS.CustomViewManager,
            `getCustomViewAttachments(${customViewId})`,
        );

        try {
            const contents =
                await this._getMailViewContentsForView(customViewId);
            const raw = contents.flatMap((c) => c.attachments ?? []);
            const deduped = dedupAttachments(raw);
            logger.info(
                LOGS.AB1727,
                `[AB#1727-SDK] getCustomViewAttachments(view=${customViewId}) mails=${contents.length} raw=${raw.length} deduped=${deduped.length}`,
            );
            return deduped;
        } catch (error) {
            logger.error(
                LOGS.CustomViewManager,
                "Error retrieving custom view attachments",
                error,
            );
            throw error;
        }
    }

    /**
     * Récupère l'INTÉGRALITÉ des liens d'une customView, dédupliqués au read par URL normalisée
     * (`computeDedupKey`, même normalisation que le SDK/front).
     * @param customViewId ID de la view
     * @returns Promise avec tous les liens dédupliqués de la view
     */
    public async getCustomViewLinks(
        customViewId: string,
    ): Promise<LinkObject[]> {
        logger.info(
            LOGS.CustomViewManager,
            `getCustomViewLinks(${customViewId})`,
        );

        try {
            const contents =
                await this._getMailViewContentsForView(customViewId);
            const raw = contents.flatMap((c) => c.links ?? []);
            const deduped = dedupLinks(raw);
            logger.info(
                LOGS.AB1727,
                `[AB#1727-SDK] getCustomViewLinks(view=${customViewId}) mails=${contents.length} raw=${raw.length} deduped=${deduped.length}`,
            );
            return deduped;
        } catch (error) {
            logger.error(
                LOGS.CustomViewManager,
                "Error retrieving custom view links",
                error,
            );
            throw error;
        }
    }

    /**
     * Récupère les événements FUTURS d'une customView. Le filtre « passé » est appliqué au read
     * (relatif au temps courant) sur les bundles bruts par-mail.
     * @param customViewId ID de la view
     * @returns Promise avec les événements futurs de la view
     */
    public async getCustomViewEvents(
        customViewId: string,
    ): Promise<LinkedEventDetails[]> {
        logger.info(
            LOGS.CustomViewManager,
            `getCustomViewEvents(${customViewId})`,
        );

        try {
            const contents =
                await this._getMailViewContentsForView(customViewId);
            const raw = contents.flatMap((c) => c.events ?? []);
            const future = filterFutureEvents(raw);
            logger.info(
                LOGS.AB1727,
                `[AB#1727-SDK] getCustomViewEvents(view=${customViewId}) mails=${contents.length} raw=${raw.length} future=${future.length}`,
            );
            return future;
        } catch (error) {
            logger.error(
                LOGS.CustomViewManager,
                "Error retrieving custom view events",
                error,
            );
            throw error;
        }
    }

    /**
     * Update the note content of a custom view
     * @param customViewId ID of the view to update
     * @param noteContent New note content
     * @returns Promise with updated CustomView object
     */
    public async updateNoteContent(
        customViewId: string,
        noteContent: string,
    ): Promise<CustomView> {
        logger.info(LOGS.CustomViewManager, "updateNoteContent()");

        try {
            const result: CustomView = await this.apiManager.updateNoteContent(
                customViewId,
                noteContent,
            );
            return result;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            if (errorMessage.includes("NO_VALID_LICENSE_FOR_CUSTOM_VIEW")) {
                this.eventBus.emit("no-valid-license-for-custom-view");
                throw error;
            }
            logger.error(
                LOGS.CustomViewManager,
                "Error updating note content",
                error,
            );
            throw error;
        }
    }

    /**
     * Generates a ViewFilter based on user natural language prompt
     * @param userPrompt - Text entered by the user describing the desired view filter
     * @returns A Promise that resolves to a ViewFilter object with keywords and date range
     */
    public async generateViewFilter(userPrompt: string): Promise<ViewFilter> {
        logger.info(LOGS.CustomViewManager, "generateViewFilter ... ");
        this._assertHasValidLicenseForCustomView();

        try {
            const viewFilter: ViewFilter =
                await this.iaManager.generateViewFilter(userPrompt);

            // Suppression des keywords dupliqués dans des identified users
            if (viewFilter.keywords?.length) {
                const allIdentifiedUsers = [
                    ...(viewFilter.identifiedSenders || []),
                    ...(viewFilter.identifiedRecipients || []),
                ];

                viewFilter.keywords = viewFilter.keywords.filter(
                    (keyword) =>
                        !allIdentifiedUsers.some(
                            (user) =>
                                user
                                    .toLowerCase()
                                    .includes(keyword.toLowerCase()) ||
                                keyword
                                    .toLowerCase()
                                    .includes(user.toLowerCase()),
                        ),
                );
            }

            // Résolution des senders / recipients en emails triés par pertinence
            if (viewFilter.identifiedSenders?.length) {
                viewFilter.identifiedSenders =
                    await this._resolveIdentifiedUsers(
                        viewFilter.identifiedSenders,
                    );
            }

            if (viewFilter.identifiedRecipients?.length) {
                viewFilter.identifiedRecipients =
                    await this._resolveIdentifiedUsers(
                        viewFilter.identifiedRecipients,
                    );
            }

            return viewFilter;
        } catch (error) {
            const errorMessage =
                error instanceof Error ? error.message : String(error);
            if (errorMessage.includes("NO_VALID_LICENSE_FOR_CUSTOM_VIEW")) {
                this.eventBus.emit("no-valid-license-for-custom-view");
                throw error;
            }
            if (errorMessage.includes("MONTHLY_TOKEN_QUOTA_EXCEEDED")) {
                this.eventBus.emit("max-ia-tokens-reached");
                throw error;
            }
            logger.error(
                LOGS.CustomViewManager,
                `Error generating view filter (userPrompt: "${userPrompt}")`,
                error,
            );
            throw error;
        }
    }

    /**
     * Résout une liste de noms d'utilisateurs (saisis par l'IA dans un ViewFilter)
     * en adresses email, dédupliquées et triées par score de pertinence décroissant.
     *
     * Pour chaque nom, on interroge les contacts via `ContactManager.resolveContacts`,
     * puis on classe les emails trouvés selon le score des « personnes » du contexte
     * utilisateur (`UserContextLoader.refreshUserContext`).
     *
     * Factorise les anciens blocs senders/recipients de `generateViewFilter`
     * (qui étaient dupliqués, le bloc recipients oubliant de trier le résultat).
     *
     * @param userNames - Noms/identifiants bruts à résoudre (senders ou recipients)
     * @returns Les emails correspondants, dédupliqués et triés par score décroissant
     */
    private async _resolveIdentifiedUsers(
        userNames: string[],
    ): Promise<string[]> {
        const searchPromises = userNames.map(async (userName) => {
            const { emails, source } =
                await this.contactManager.resolveContacts(userName);
            // AB#1634 : quand la source est RAG, la pertinence remplace l'heuristique no-reply → on NE
            // filtre PAS. En legacy (AB#1744), on garde le comportement historique : un no-reply n'est
            // retenu que s'il est l'identifiant réel du terme cherché (`noreply@jotform.com` / `lucca.fr`),
            // et écarté s'il n'est qu'un canal de notification (`no-reply@teams.mail.microsoft`).
            return source === "rag"
                ? emails
                : filterNoReplyContactsForQuery(emails, userName);
        });

        const allFound = await Promise.all(searchPromises);

        // Suppression des doublons
        const identifiedUsers = [...new Set(allFound.flat())];

        const userContext = await this.contextLoader.refreshUserContext();

        // Score par défaut 0, puis mise à jour depuis le contexte utilisateur
        const scoreMap = new Map<string, number>();
        identifiedUsers.forEach((user) => scoreMap.set(user, 0));

        userContext?.personnes?.forEach((person) => {
            person.items?.forEach((email) => {
                if (scoreMap.has(email)) {
                    scoreMap.set(email, person.score);
                }
            });
        });

        // Tri par score descendant
        identifiedUsers.sort(
            (a, b) => (scoreMap.get(b) ?? 0) - (scoreMap.get(a) ?? 0),
        );

        return identifiedUsers;
    }

    /**
     *  Returns the user specific View Id for a given SmartLabel customView
     *
     * @param viewName - SmartLabel name for the CustomView
     * @returns
     */
    public async _getSmartLabelCustomViewId(viewName: string): Promise<string> {
        // TODO - This trick is not clean, we should ensure init is doen before this function can be called in MailManager._updateAllMailsWithContext
        if (!this.isInitialisationDone) {
            logger.warn(
                LOGS.CustomViewManager,
                `_getSmartLabelCustomViewId called before _initialize() Done - Should not happen, check startup lifecycle !`,
            );
            while (!this.isInitialisationDone) {
                await new Promise((resolve) => setTimeout(resolve, 50)); // Attendre 50ms entre chaque vérification
            }
        }

        const smartLabelView = Object.values(
            this.viewState.viewsRecordById,
        ).find((customView) => customView.name === viewName);

        if (!smartLabelView) {
            const error = `getSmartLabelCustomViewId : no customView with name (${viewName}) found in Record !`;
            logger.error(LOGS.CustomViewManager, error);
            throw new Error(error);
        }
        if (!smartLabelView.id) {
            const error = `getSmartLabelCustomViewId : customView with name (${viewName}) found with null id !`;
            logger.error(LOGS.CustomViewManager, error);
            throw new Error(error);
        }

        return smartLabelView.id;
    }

    /**
     * @method _getCustomViewType
     * Retourne le type d'une CustomView à partir de son ID (inverse de _getSmartLabelCustomViewId).
     * Accès direct O(1) dans viewsRecordById — ne nécessite pas d'attendre l'init car
     * cette méthode n'est appelée qu'après que les vues soient chargées.
     *
     * @param customViewId - Identifiant de la vue
     * @returns CustomViewType de la vue, ou null si l'ID est inconnu
     */
    public _getCustomViewType(customViewId: string): CustomViewType | null {
        return this.viewState.viewsRecordById[customViewId]?.viewType ?? null;
    }

    // #endregion CustomView Object and properties management

    // #region CustomView content management

    /**
     * Vérifie que l'utilisateur dispose d'une licence valide pour les mutations custom view.
     * @throws Error si la licence n'est pas valide
     */
    private _assertHasValidLicenseForCustomView(): void {
        if (
            !this.featureFlags.isEnabled(FeatureFlag.CUSTOMVIEW_ENABLE_CREATION)
        ) {
            logger.error(
                LOGS.CustomViewManager,
                "No valid license for custom view mutation — operation not permitted",
            );
            this.eventBus.emit("no-valid-license-for-custom-view");
            throw new Error("NO_VALID_LICENSE_FOR_CUSTOM_VIEW");
        }
    }

    // #endregion CustomView content management
}
